/**
 * Title (08 §2.1): the logo, the main menu, the version string. The first entry is CONTINUE
 * when an operation is active, NEW OPERATION otherwise; the diorama behind it is the app's. TRAINING
 * (ROUND7 §5) stands above them all until this browser has finished the course once.
 * Under the logo, one dim line when the cloud sync has something the player should know before he
 * plays on (cloud/cloudNotice.ts): it has paused, or it has just settled two saves into one.
 */

import './title.css';
import { trainingDeployment } from '../../meta/training';
import { changesSeen, CURRENT_VERSION, markChangesSeen } from './changes/changelog';
import { cloudNotice } from './cloud/cloudNotice';
import type { Ctx, Screen } from './context';
import { box, button, el, show } from './dom';
import { difficultyName, requisitionLine } from './text';
import { trainingDone } from './trainingFlag';
import { frame } from './widgets/frame';
import { navHints } from './widgets/keyHints';

/**
 * Entries that lead to another mission: hidden while one is suspended. TRAINING is not one of them (ROUND7 §5):
 * the course never touches a held mission's suspend save, so it stays on offer.
 */
const FLIES: readonly string[] = ['CLASSIC', 'PRACTICE', 'DAILY DROP'];

/** Shown bottom-right, and opens the patch notes: the newest entry in CHANGELOG.md (changes/changelog.ts). */
export const VERSION = `V${CURRENT_VERSION}`;

/**
 * The cloud line is capped at 300 px and wraps, but the port's `message` is arbitrary text from a server:
 * a long one pushes the logo off centre, and one long unbroken token (a save code, a URL) runs straight past
 * the cap. Three lines at x1 is as much as the title can carry, so it is cut there, on a word, with an
 * ellipsis - the whole story is on the Options screen, which is where the fallback text sends him.
 */
export const CLOUD_LINE_MAX = 150;

export function cloudLineText(notice: string): string {
  const text = notice.replace(/\s+/g, ' ').trim();
  if (text.length <= CLOUD_LINE_MAX) return text;
  const cut = text.slice(0, CLOUD_LINE_MAX);
  const space = cut.lastIndexOf(' ');
  return `${(space > CLOUD_LINE_MAX - 24 ? cut.slice(0, space) : cut).trimEnd()}...`;
}

type Entry = [label: string, note: string, go: () => void];

/** The title's note for TRAINING until the course has been finished once in this browser (ROUND7 §5). */
export const TRAINING_NEW_NOTE = 'NEW? START HERE. A SHORT FIELD COURSE: EVERY CONTROL, AND THE RULES THAT MATTER.';

/**
 * TRAINING is always on the title (ROUND7 §5) - even with a ranger held in the field, because the course never
 * touches that mission's suspend save. First in the list until it has been finished once in this browser, then
 * just below PRACTICE (or below the first entry while PRACTICE is hidden). It flies straight from here: no
 * briefing, no loadout.
 */
function placeTraining(entries: Entry[], ctx: Ctx): void {
  const done = trainingDone();
  const note = done ? 'THE FIELD COURSE AGAIN: THE DROP, THE KNIFE, THE TRENCH, THE DOG, THE RANGE.' : TRAINING_NEW_NOTE;
  const entry: Entry = ['TRAINING', note, () => ctx.fly(trainingDeployment())];
  if (!done) {
    entries.unshift(entry);
    return;
  }
  const practice = entries.findIndex(([label]) => label === 'PRACTICE');
  entries.splice(practice >= 0 ? practice + 1 : Math.min(1, entries.length), 0, entry);
}

export function titleScreen(ctx: Ctx): Screen {
  const profile = ctx.api.profile();
  const op = profile.operation !== null && profile.operation.status === 'active' ? profile.operation : null;
  const f = frame('title', 'FIELD TERMINAL', navHints(null));
  // The version is a button: it opens the patch notes, and says NEW until this version's have been read.
  const label = changesSeen() ? VERSION : `${VERSION} - NEW`;
  // Read when he opens them from here - the one real way in; a `?ui=changes` fixture writes nothing.
  const version = button(label, () => {
    markChangesSeen();
    ctx.nav.changes();
  }, 'menu-hints-note menu-version');
  version.title = 'WHAT CHANGED';
  f.el.querySelector('.menu-hints')?.append(version);
  f.info.append(el('span', '', `COMMENDATIONS ${profile.commendations}`));

  const logo = box(
    'menu-title-logo',
    el('div', 'menu-title-word', 'NIGHTDROP'),
    el('div', 'menu-title-tag', 'ONE RANGER GOES IN ALONE'),
  );

  // A ranger is in the field: the only way on is back to him. Nothing here may fly another mission.
  const held = ctx.suspended?.() ?? null;
  const entries: Entry[] = [
    held !== null
      ? ['RESUME MISSION', held.label, () => held.resume()]
      : op === null
      ? ['NEW OPERATION', 'A CAMPAIGN OF TEN TO TWELVE MISSIONS. PERMADEATH.', () => ctx.nav.newOperation()]
      : [
          'CONTINUE OPERATION',
          `${op.codename} - ${difficultyName(op.difficulty)} - ${requisitionLine(op.rp)} - READINESS ${op.readiness}/10`,
          () => ctx.nav.theatre(),
        ],
    ['CLASSIC', 'THE 1987 STRUCTURE: SIX-SLOT ROSTER, TWELVE MISSIONS, ORIGINAL RULES.', () => ctx.nav.classic()],
    ['PRACTICE', 'ANY MISSION, ANY GROUND, NOTHING AT STAKE.', () => ctx.nav.practice()],
    ['DAILY DROP', "TODAY'S MISSION, THE SAME FOR EVERYONE. ONE ATTEMPT.", () => ctx.nav.daily()],
    ['PERSONNEL', 'EVERY RANGER YOU HAVE KNOWN, AND EVERY MISSION HE FLEW.', () => ctx.nav.personnel()],
    ['HALL OF HONOR', `${profile.hall.length}/6 LIVING VETERANS.`, () => ctx.nav.hall()],
    ['MEMORIAL WALL', `${profile.memorial.length} NAMES.`, () => ctx.nav.memorial()],
    ['RECORDS', 'BESTS FOR EVERY MISSION, STREAKS AND TOTALS.', () => ctx.nav.records()],
    ['OPTIONS', 'SOUND, TEXT SIZE, COMFORT, ACCESSIBILITY, CONTROLS.', () => ctx.nav.options()],
    ['CREDITS', '', () => ctx.nav.credits()],
  ].filter(([label]) => held === null || !FLIES.includes(label as string)) as typeof entries;
  placeTraining(entries, ctx);

  const note = el('div', 'menu-title-note menu-prose menu-dim');
  const list = el('div', 'menu-title-menu menu-scroll');
  let first: HTMLButtonElement | null = null;
  let resume: HTMLButtonElement | null = null;
  for (const [label, text, go] of entries) {
    const b = button(label, go, 'menu-title-entry');
    b.addEventListener('focus', () => (note.textContent = text));
    b.addEventListener('mouseenter', () => (note.textContent = text));
    list.append(b);
    first ??= b;
    if (label === 'RESUME MISSION') resume = b;
  }

  // The port is optional (no save server, a fixture): without it there is no line, and nothing to unsubscribe.
  const port = ctx.cloud;
  const left = box('menu-title-left', logo);

  // How many campaigns have seen action lately (`GET /api/pulse`). Asked for in play. The server
  // never opens a save, so it counts saves WRITTEN - which is "somebody played", and the line says
  // exactly that rather than claiming to know who is in the field this minute.
  const wall = ctx.wall;
  if (wall !== undefined) {
    const pulse = el('div', 'menu-title-pulse menu-prose menu-dim menu-small menu-hidden');
    left.append(pulse);
    void (async () => {
      const seen = await wall.pulse().catch(() => null);
      if (seen === null || seen.week === 0) return;
      const day = seen.day === 1 ? '1 CAMPAIGN' : `${seen.day} CAMPAIGNS`;
      pulse.textContent = `${day} SAW ACTION IN THE LAST 24 HOURS - ${seen.week} THIS WEEK.`;
      show(pulse, true);
    })();
  }
  let unsubscribe: () => void = () => undefined;
  if (port !== undefined) {
    const cloud = el('div', 'menu-title-cloud menu-prose menu-dim menu-small menu-hidden');
    cloud.setAttribute('role', 'status');
    const renderCloud = (): void => {
      let text: string | null = null;
      try {
        text = cloudNotice(port.status());
      } catch {
        // A port that throws must not take the title - the root of every flow - down with it.
      }
      cloud.textContent = text === null ? '' : cloudLineText(text);
      show(cloud, text !== null);
    };
    left.append(cloud);
    try {
      unsubscribe = port.subscribe(renderCloud);
    } catch {
      // No live updates, then: the line still says what was true when the title opened.
    }
    renderCloud();
  }

  f.body.append(left, box('menu-title-right', list, note));
  return {
    el: f.el,
    // The title is the root of the flow: Esc has nowhere to go.
    back: () => undefined,
    // A ranger held in the field keeps Enter: TRAINING may stand above him in the list, not under his finger.
    initialFocus: () => resume ?? first,
    dispose: () => unsubscribe(),
  };
}
